import { FC } from 'react';

import {
  Flex,
  Heading,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tag,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import { ParsedHTMLText } from '@/components/functional';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { CaseStudyTagRelationResponseCollection, ImageFragment, Maybe } from '@/graphql/generated';
import { LOCALES } from '@/utils/constants';
import { getImageAlt, getImageSrcWithBlurhash } from '@/utils/images';
import { normalizeUrl } from '@/utils/urls';

type CaseStudyHeaderProps = {
  title: string;
  description?: string | null;
  coverImage: ImageFragment;
  client: string;
  clientTooltip?: string | null;
  location: string;
  website?: string | null;
  projectStatus?: string | null;
  projectStartDate?: string | null;
  tags?: Maybe<CaseStudyTagRelationResponseCollection>;
};

export const CaseStudyHeader: FC<CaseStudyHeaderProps> = ({
  title,
  description,
  coverImage,
  client,
  clientTooltip,
  location,
  website,
  projectStatus,
  projectStartDate,
  tags,
}) => {
  const { height, width } = coverImage?.data?.attributes ?? {};
  const { t } = useTranslation(LOCALES.CASE_STUDY);

  return (
    <SectionWrapper bg="surface.500">
      <SectionContentWrapper pb={{ base: 12, lg: 16 }} flexDir="column" display="flex" alignItems="center">
        <VStack spacing="4">
          <Heading
            as="h1"
            variant={{
              base: 'component-heading-large/2xl',
              sm: 'component-heading-large/3xl',
              lg: 'component-heading-large/6xl',
            }}
            color="text.500"
            textAlign="center"
          >
            <ParsedHTMLText content={title} />
          </Heading>
          {description ? (
            <Text
              as="span"
              variant={{
                base: 'text-md/lineHeight-6/font-normal',
                sm: 'text-md/lineHeight-6/font-normal',
              }}
              textAlign="center"
              color="text.500"
              maxW={{
                base: '100%',
                sm: '85%',
                lg: '70%',
              }}
            >
              <ParsedHTMLText content={description} />
            </Text>
          ) : null}
        </VStack>
        <Flex width="full" mt="12" mb="6" maxW="1280px">
          <Image
            height={height ?? undefined}
            width={width ?? undefined}
            {...getImageSrcWithBlurhash(coverImage?.data?.attributes)}
            alt={getImageAlt(coverImage?.data?.attributes, title)}
            style={{ objectFit: 'cover', width: '100%' }}
          />
        </Flex>
        <Wrap
          spacingX={{
            base: 8,
            md: 14,
            lg: 28,
          }}
          spacingY={{
            base: 6,
            md: 8,
            lg: 10,
          }}
          maxW="container.lg"
          margin="0 auto"
        >
          <WrapItem>
            <VStack alignItems="flex-start">
              <Text variant="text-sm/lineHeight-5/font-semibold" color="text.300">
                {t('header.client')}
              </Text>
              {clientTooltip ? (
                <Popover trigger="hover" placement="top">
                  <PopoverTrigger>
                    <Text variant="text-sm/lineHeight-5/font-normal" color="text.500">
                      {client}
                    </Text>
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverBody>{clientTooltip}</PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : (
                <Text variant="text-sm/lineHeight-5/font-normal" color="text.500">
                  {client}
                </Text>
              )}
            </VStack>
          </WrapItem>
          <WrapItem>
            <VStack alignItems="flex-start">
              <Text variant="text-sm/lineHeight-5/font-semibold" color="text.300">
                {t('header.location')}
              </Text>
              <Text variant="text-sm/lineHeight-5/font-normal" color="text.500">
                {location}
              </Text>
            </VStack>
          </WrapItem>
          {website ? (
            <WrapItem>
              <VStack alignItems="flex-start">
                <Text variant="text-sm/lineHeight-5/font-semibold" color="text.300">
                  {t('header.website')}
                </Text>
                <Text
                  as={Link}
                  variant="text-sm/lineHeight-5/font-normal"
                  color="text.500"
                  href={normalizeUrl(website)}
                  isExternal
                >
                  {website}
                </Text>
              </VStack>
            </WrapItem>
          ) : null}
          {projectStartDate ? (
            <WrapItem>
              <VStack alignItems="flex-start">
                <Text variant="text-sm/lineHeight-5/font-semibold" color="text.300">
                  {t('header.cooperationDates')}
                </Text>
                <Text variant="text-sm/lineHeight-5/font-normal" color="text.500">
                  {projectStartDate}
                </Text>
              </VStack>
            </WrapItem>
          ) : null}
          {projectStatus ? (
            <WrapItem>
              <VStack alignItems="flex-start">
                <Text variant="text-sm/lineHeight-5/font-semibold" color="text.300">
                  {t('header.projectStatus')}
                </Text>
                <Text variant="text-sm/lineHeight-5/font-normal" color="text.500">
                  {projectStatus}
                </Text>
              </VStack>
            </WrapItem>
          ) : null}
          <WrapItem>
            <VStack alignItems="flex-start">
              <Text variant="text-sm/lineHeight-5/font-semibold" color="text.300">
                {t('header.workScope')}
              </Text>
              <Flex mb="6" gap="2" wrap="wrap" justify="flex-start">
                {tags?.data?.map((tag) => (
                  <Tag size="md" variant="casestudy" key={tag.id}>
                    {tag.attributes?.name}
                  </Tag>
                ))}
              </Flex>
            </VStack>
          </WrapItem>
        </Wrap>
      </SectionContentWrapper>
    </SectionWrapper>
  );
};
